/**
 * O que faz: Mostra a conversão por origem do contato e explica, em painel recolhível,
 *            como ler a taxa de conversão e o faturamento.
 * Onde usa: Dashboard principal, no fim da página (antes ficava logo abaixo dos indicadores).
 * Conexões: Recebe conversaoPorOrigem calculada pelo dashboardService; Collapsible compartilhado.
 * Tabelas: Não acessa o banco diretamente; exibe agregações de leads e agendamentos.
 * Data: 22/07/2026 (recolhível no design system Gens em 05/10/2026).
 */

import { useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { ConversionByOriginMetrics } from '@/hooks/dashboard/types';

interface ConversionAttributionNoticeProps {
  metrics: ConversionByOriginMetrics;
  periodLabel: string;
}

const formatMetric = (metric: ConversionByOriginMetrics['anuncios']) =>
  `${metric.convertidos}/${metric.contatos}`;

export const ConversionAttributionNotice = ({
  metrics,
  periodLabel,
}: ConversionAttributionNoticeProps) => {
  // Começa fechado: o texto explicativo é consulta ocasional e ocupava a área nobre do dashboard.
  const [aberto, setAberto] = useState(false);
  const origens = [
    { rotulo: 'Leads de anúncios', metrica: metrics.anuncios },
    { rotulo: 'Links rastreados', metrica: metrics.links },
    { rotulo: 'Outros ou sem rastreamento', metrica: metrics.outros },
  ];

  return (
    <Collapsible open={aberto} onOpenChange={setAberto}>
      <section aria-label="Conversão por origem" className="rounded-2xl border border-border bg-card p-4 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)] sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Conversão por origem</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Contatos com agendamento ÷ contatos criados em “{periodLabel}”</p>
          </div>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="inline-flex min-h-9 shrink-0 items-center gap-1.5 self-start rounded-full px-3 text-xs font-semibold text-accent-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none lg:self-auto"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
              Como ler a taxa de conversão
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none', aberto && 'rotate-180')} aria-hidden="true" />
            </button>
          </CollapsibleTrigger>
        </div>

        {/* Os três números ficam sempre visíveis: são o que importa para avaliar campanhas. */}
        <dl className="mt-3 grid gap-2 sm:grid-cols-3">
          {origens.map(({ rotulo, metrica }) => (
            <div key={rotulo} className="rounded-xl bg-muted px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">{rotulo}</dt>
              <dd className="mt-0.5 flex items-baseline gap-1.5">
                <span className="lg-display text-lg font-bold tabular-nums text-foreground">{metrica.taxa.toLocaleString('pt-BR')}%</span>
                <span className="text-xs tabular-nums text-muted-foreground">{formatMetric(metrica)}</span>
              </dd>
            </div>
          ))}
        </dl>

        <CollapsibleContent className="overflow-hidden">
          {/* Entrada com fade + leve deslize (só opacidade/transform); some sem animação com movimento reduzido. */}
          <div className="mt-3 space-y-2 border-t border-border pt-3 text-xs leading-5 text-muted-foreground animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none">
            <p>
              No período “{periodLabel}”, a taxa mostra quantos contatos criados possuem ao menos um agendamento não cancelado.
              Consultas agendadas e realizadas usam a data da consulta dentro do período selecionado.
            </p>
            <p>
              O faturamento soma consultas com status Pago (pela data em que o pagamento foi marcado)
              e vendas registradas no Kanban ou no chat (pela data da venda, não pela data de criação do lead).
              A quebra mostra PIX, cartão e as demais formas da agenda, além das vendas do CRM.
              Se a mesma compra for marcada como Pago na agenda e também registrada no Kanban, o valor entra duas vezes.
            </p>
            <p>
              Contatos criados manualmente na agenda/CRM e conversas gerais entram em “Outros ou sem rastreamento”.
              Para avaliar campanhas, use a taxa de leads de anúncios, não a taxa geral.
            </p>
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};
